<template>
  <v-container fluid class="ma-0 pa-0">
    <v-toolbar flat color="toolbar">
      <v-breadcrumbs large :items="breadcrumbs">
        <template #item="{ item }">
          <v-breadcrumbs-item
            :href="item.href"
            :disabled="item.disabled">
            <span :class="!item.disabled ? 'primary--text' : ''">
              {{ item.text }}
            </span>
          </v-breadcrumbs-item>
        </template>
      </v-breadcrumbs>

      <v-divider vertical inset class="mx-4" />

      <v-row align="center" class="flex-nowrap" dense>
        <v-col align="start" class="flex-nowrap flex-shrink-1" >
          <span class="text-subtitle-1">
            <span class="font-weight-medium">
              Ad Dates:
            </span>
            <span style="margin-left: 2px">
              {{ formatDateDisplay(adPlanner && adPlanner.ad_start_date) }} - {{ formatDateDisplay(adPlanner && adPlanner.ad_end_date) }}
            </span>
          </span>
        </v-col>

        <!-- Column Visibility Button -->
        <v-menu
          :close-on-content-click="false"
          :nudge-bottom="3"
          :value="columnVisability"
          bottom
          offset-y>
          <template #activator="{ on: menu, attrs }">
            <v-tooltip top>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn
                  @click="openColumnVisability"
                  class="grey lighten-2 ml-2"
                  v-bind="attrs"
                  v-on="{ ...tooltip, ...menu }">
                  <v-icon>mdi-cog-outline</v-icon>
                </v-btn>
              </template>
              <span>Column Visability</span>
            </v-tooltip>
          </template>
          <v-card class="fill" width="250">
            <v-card-title class="pb-0 text-subtitle-1">Select Visable Headers</v-card-title>
            <v-card-text style="max-height: 500px; overflow-y: auto;">
              <v-list dense>
                <v-list-item
                  v-for="header in selectedHeaders"
                  :key="header.value">
                  <v-checkbox 
                    v-model="header.show"
                    :label="header.text"
                    dense
                    hide-details>

                  </v-checkbox>
                </v-list-item>
              </v-list>
            </v-card-text>
            <v-card-actions class="fill">
              <v-spacer />
              <v-btn
                text
                @click="closeColumnVisability">
                Cancel
              </v-btn>
              <v-btn
                :disabled="selectedHeaders.every(header => header.show == true)"
                class="warning"
                @click="resetCustomHeaders">
                Reset
              </v-btn>
              <v-btn
                class="blue white--text mr-2"
                @click="saveCustomHeaders">
                Save
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>

        <!-- Bulk Edit Button -->
        <v-menu
          :close-on-content-click="false"
          :nudge-bottom="3"
          :value="bulkEditMenu"
          bottom
          offset-y>
          <template #activator="{ on: menu, attrs }">
            <v-tooltip top>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn 
                  :disabled="!hasSelectedItems || isEditingDisabled"
                  :readonly="!hasSelectedItems" 
                  @click="bulkEditMenu = true"
                  class="grey lighten-2 ml-2"
                  v-bind="attrs"
                  v-on="{ ...tooltip, ...menu }">
                  <v-icon>mdi-pencil-box-multiple-outline</v-icon>
                </v-btn>
              </template>
              <span>Edit</span>
            </v-tooltip>
          </template>
          <v-card class="fill" width="200">
            <v-card-title class="pb-0 text-subtitle-1"> Edit </v-card-title>
            <v-form class="px-4 pt-4" ref="form">
              <v-text-field :rules="countRules" background-color="input" dense label="Count" outlined
                type="number" v-model="bulkCount">
              </v-text-field>
              <v-text-field
                :rules="srpRules"
                v-model="bulkSRP"
                dense
                outlined
                background-color="input"
                label="AD SRP"
                placeholder="0.00"
                prefix="$"
                type="number"
                @blur="handleBlur(null, $event)"
                @keydown.enter="bulkUpdate">
              </v-text-field>
            </v-form>
            <v-card-actions class="fill">
              <v-spacer />
              <v-btn
                text
                @click="closeBulkEdit">
                Cancel
              </v-btn>
              <v-btn 
                class="blue white--text mr-2"
                @click="bulkUpdate">
                Update
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
        
        <!-- Save Button -->
        <v-tooltip top>
          <template v-slot:activator="{ on: tooltip }">
            <v-btn  
              :loading="loading" 
              :disabled="!hasUnsavedChanges" 
              v-on="tooltip"
              color="success"
              class="mx-2"
              @click="saveChanges">
              <v-icon left>mdi-content-save</v-icon>
              Save
            </v-btn>
          </template>
          <span>Save Changes</span>
        </v-tooltip>

        <v-col class="mx-1 d-flex ml-auto" cols="4">
          <SearchFilters :departments="departments" :pages="pages" :feature_text="feature_text" :feature_type="feature_type" :order_page="false" v-model="searchParams" />
        </v-col>

        <!-- Actions Menu -->
        <v-col
          class="d-flex shrink">
          <v-menu
            bottom
            left
            offset-y
            nudge-bottom="3"
            close-on-content-click
            min-width="200">
            <template v-slot:activator="{ on: menu, attrs }">
              <v-tooltip left>
                <template v-slot:activator="{ on: tooltip }">
                  <v-btn
                    v-on="{ ...tooltip, ...menu }"
                    v-bind="attrs"
                    :disabled="readOnly">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <span>Actions</span>
              </v-tooltip>
            </template>
            <v-list>
              <v-list-item
                @click="exportCSV">
                <v-icon class="mr-2" color="blue">mdi-file-excel-outline</v-icon>
                Export
              </v-list-item>
              <v-tooltip left :disabled="!disableSendToGrid">
                <template v-slot:activator="{ on }">
                  <div v-on="on">
                    <v-list-item
                      :disabled="disableSendToGrid"
                      @click="publishToOrderGrid">
                      <v-icon class="mr-2" :color="disableSendToGrid ? 'grey' : 'green'">mdi-email-fast-outline</v-icon>
                      Send to Order Grid
                    </v-list-item>
                  </div>
                </template>
                <span>{{ tooltipText }}</span>
              </v-tooltip>
            </v-list>
          </v-menu>
        </v-col>
      </v-row>
    </v-toolbar>

    <v-data-table
      :headers="displayHeaders"
      :height="tableSize"
      :items-per-page="pageSize"
      :items.sync="groupedItems"
      :group-by="'groupKey'"
      :loading="loading"
      :sort-by="['page_name', 'position_name']"
      v-model="selectedItems"
      show-select
      ref="ad-items-table"
      dense
      fixed-header
      hide-default-footer
      loading-text="Loading... Please Wait"
      no-data-text="No Retail Ad Items to Display"
      no-results-text="No Matches Found"
      item-key="ad_plan_id">

      <template #[`group.header`]="{ group, headers, items, isOpen, toggle }">
        <td
          :colspan="headers.length"
          style="
            height: 40px;
            border-top: 1px solid grey !important;
          "
          class="blue-grey lighten-2 white--text">
          <v-row dense align="center">
            <v-btn dark small icon
              :ref="group"
              :data-open="isOpen"
              @click="toggle">
              <v-icon>
                {{ isOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </v-btn>
            <span class="ml-1 font-weight-medium">
              {{ items[0].groupName }}
            </span>
            <v-divider inset vertical length="10" class="mx-4 mb-1 white" />
            <span>
              {{  items[0].feature_notes }}
            </span>
          </v-row>
        </td>
      </template>

      <template #[`item.item_desc`]="{ item }">
        <td class="noWrap px-2">
          {{ item.item_desc }}
        </td>
      </template>

      <template #[`item.department`]="{ item }">
        <td class="noWrap px-2">
          {{ item.department }}
        </td>
      </template>

      <template #[`item.qty_per_case`]="{ item }">
        <td class="noWrap px-2">
          {{ item.qty_per_case }}
        </td>
      </template>

      <template #[`item.pack_size`]="{ item }">
        <td class="noWrap px-2">
          {{ item.pack_size }}
        </td>
      </template>

      <template #[`item.upc`]="{ item }">
        <td class="noWrap px-2">
          {{ item.upc }}
        </td>
      </template>

      <template #[`item.case_cost`]="{ item }">
        <td class="noWrap px-2">
          ${{ item.case_cost | format }}
        </td>
      </template>

      <template #[`item.net_case_cost`]="{ item }">
        <td class="noWrap px-2">
          ${{ item.net_case_cost | format }}
        </td>
      </template>

      <template #[`item.amap`]="{ item }">
        <td class="noWrap px-2">
          ${{ item.amap | format }}
        </td>
      </template>

      <template #[`item.early_buy_billback`]="{ item }">
        <td class="noWrap px-2">
          ${{ item.early_buy_billback | format }}
        </td>
      </template>

      <template #[`item.dsd_oi_billback`]="{ item }">
        <td class="noWrap px-2">
          ${{ item.dsd_oi_billback | format }}
        </td>
      </template>

      <template #[`item.ad_billback`]="{ item }">
        <td class="noWrap px-2">
          ${{ item.ad_billback | format }}
        </td>
      </template>

      <template #[`item.tpr_billback`]="{ item }">
        <td class="noWrap px-2">
          ${{ item.tpr_billback | format }}
        </td>
      </template>

      <template #[`item.edlc_billback`]="{ item }">
        <td class="noWrap px-2">
          ${{ item.edlc_billback | format }}
        </td>
      </template>

      <template #[`item.ad_scan`]="{ item }">
        <td class="noWrap px-2">
          ${{ item.ad_scan | format }}
        </td>
      </template>

      <template #[`item.epay_ad_scan`]="{ item }">
        <td class="noWrap px-2">
          ${{ item.epay_ad_scan | format }}
        </td>
      </template>

      <template #[`item.ad_scan_compete`]="{ item }">
        <td class="noWrap px-2">
          ${{ item.ad_scan_compete | format }}
        </td>
      </template>

      <template #[`item.tpr_scan`]="{ item }">
        <td class="noWrap px-2">
          ${{ item.tpr_scan | format }}
        </td>
      </template>

      <template #[`item.epay_tpr_scan`]="{ item }">
        <td class="noWrap px-2">
          ${{ item.epay_tpr_scan | format }}
        </td>
      </template>

      <template #[`item.tpr_scan_compete`]="{ item }">
        <td class="noWrap px-2">
          ${{ item.tpr_scan_compete | format }}
        </td>
      </template>

      <template #[`item.edlc_scan`]="{ item }">
        <td class="noWrap px-2">
          ${{ item.edlc_scan | format }}
        </td>
      </template>

      <template #[`item.epay_edlc_scan`]="{ item }">
        <td class="noWrap px-2">
          ${{ item.epay_edlc_scan | format }}
        </td>
      </template>

      <template #[`item.edlc_scan_compete`]="{ item }">
        <td class="noWrap px-2">
          ${{ item.edlc_scan_compete | format }}
        </td>
      </template>

      <template #[`item.net_unit_cost`]="{ item }">
        <td class="noWrap px-2">
          ${{ item.net_unit_cost | format }}
        </td>
      </template>

      <template #[`item.count_and_retail`]="{ item }">
        <td class="px-2">
          <v-row class="my-1 px-2">
            <v-col class="pa-0 flex-grow-1">
              <v-text-field
                :disabled="isEditingDisabled"
                dense
                outlined
                hide-details
                solo
                type="number"
                v-model.number="item.ad_retail_count"
                @input="handleChange(item, {target: {value: $event}})"
                >
              </v-text-field>
            </v-col>
            <v-icon size="20" class="px-2">mdi-slash-forward</v-icon>
            <v-col class="pa-0 flex-grow-0" cols="5">
              <v-text-field
                :disabled="isEditingDisabled"
                dense
                outlined
                hide-details
                solo
                type="number"
                prefix="$"
                step="0.01"
                v-model.number="item.ad_retail"
                @input="handleChange(item, {target: {value: $event}})"
                @blur="handleBlur(item, $event)"
                >
              </v-text-field>
            </v-col>
            <v-col class="pa-0 flex-grow-0">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                <v-btn
                  @click="undoChanges(item)"
                  icon
                  v-on="on"
                  :style="{ visibility: unsaved_changes.some(change => change.item_id === item.item_id) ? 'visible' : 'hidden' }">
                  <v-icon>mdi-refresh-circle</v-icon>
                </v-btn>
                </template>
                <span>Reset Values</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </td>
      </template>

      <template #[`item.regular_retail`]="{ item }">
        <td class="noWrap px-2">
          ${{ item.regular_retail | format }}
        </td>
      </template>

      <template #[`item.last_90_days_shipped_qty`]="{ item }">
        <td class="noWrap px-2">
          {{ item.last_90_days_shipped_qty ?? '0' }}
        </td>
      </template>

      <template #[`item.ad_gross_profit_percent`]="{ item }">
        <td class="noWrap px-2">
          {{ item.ad_gross_profit_percent | format}}%
        </td>
      </template>

      <template #[`item.tpr_dates`]="{ item }">
        <td class="noWrap px-2">
          {{ item.tpr_start_date ? item.tpr_start_date + ' - ' + item.tpr_end_date : ""}}
        </td>
      </template>

      <template #[`item.x`]="{ item }">
        <td class="noWrap px-2">
          ${{ item.x | format }}
        </td>
      </template>

      <template v-slot:footer>
        <v-divider></v-divider>
        <v-toolbar flat color="toolbar">
          <v-spacer></v-spacer>
          <v-col cols="2" style="max-width: 150px">
            <v-select class="mt-8" dense :items="pageSizes" label="Items Per Page" v-model="pageSize"
              :menu-props="{ top: true, offsetY: true, maxHeight: 500 }">
            </v-select>
          </v-col>
          <v-btn icon large @click="previous" :disabled="disablePrevious" class="mx-2">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <small>Page {{ page }}</small>
          <v-btn icon large @click="next" :disabled="disableNext" class="mx-2">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </v-toolbar>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
// api
import AdRetails from "@/axios/ad-retails"
import Attributes from "@/axios/attributes"
import Party from '@/axios/party'

// mixins
import { displayAlert } from "@/mixins/alert"
import { fullwidth } from "@/mixins/fullwidth"
import { paging } from "@/mixins/paging"
import { displayHelpers } from "@/mixins/displayHelpers"
import { userAccess } from '@/mixins/user-access'
import { utils } from '@/mixins/utils'
import { partySearch } from '@/mixins/party-search'

// third-party
import _ from 'lodash'
import moment from 'moment'

// components
const SearchFilters = () => import('@/components/SearchFilters.vue')

export default {
  name: "AdPlannerItems",

  components: { SearchFilters },
  mixins: [displayAlert, fullwidth, displayHelpers, paging, userAccess, utils, partySearch],

  data() {
    return {
      adPlanner: {},
      adPlannerParty: {},
      bulkEditMenu: false,
      bulkCount: null,
      bulkSRP: null,
      departments: [],
      edgeSubscriber: false,
      filters: [],
      feature_text: [],
      feature_type: [],
      highlighted: {},
      items: [],
      loading: false,
      meat_grid: false,
      showExportModal: false,
      pages: [],
      publishedChanges: [],
      searchParams: {},
      selectedItems: [],
      store_id: null,
      store_name: null,
      storeParties: [],
      unsaved_changes: [],
      columnVisability: false,
      headers: [
        { text: 'PAGE NAME', sortable: true, filterable: true, class: 'accent white--text', value: 'page_name', show: true, hide_from_table: true },
        { text: 'Item Code', sortable: true, filterable: true, class: 'accent white--text noWrap pl-4 pr-2 py-3', value: 'item_id', align: 'start', show: true },
        { text: 'Dept', sortable: true, filterable: true, class: 'accent white--text noWrap px-2', value: 'department', align: 'start', show: true },
        { text: 'Pack', sortable: true, filterable: true, class: 'accent white--text noWrap px-2', value: 'qty_per_case', align: 'start', show: true },
        { text: 'Size', sortable: true, filterable: true, class: 'accent white--text noWrap px-2', value: 'pack_size', align: 'start', show: true },
        { text: 'Pallet Qty', sortable: true, filterable: true, class: 'accent white--text px-2', value: 'pal_qty', align: 'start', show: true },
        { text: 'Item Desc.', sortable: true, filterable: true, class: 'accent white--text noWrap px-2', value: 'item_desc', align: 'start', show: true },
        { text: 'UPC', sortable: true, filterable: true, class: 'accent white--text noWrap px-2', value: 'upc', align: 'start', show: true },
        { text: 'Case Cost', sortable: true, filterable: true, class: 'accent white--text noWrap px-2', value: 'case_cost', align: '', show: true },
        { text: 'Net Case Cost', sortable: true, filterable: true, class: 'accent white--text noWrap px-2', value: 'net_case_cost', align: '', show: true },
        { text: 'AMAP', sortable: true, filterable: true, class: 'accent white--text px-2', value: 'amap', align: 'start', show: true },
        { text: 'EBA', sortable: true, filterable: true, class: 'accent white--text px-2', value: 'early_buy_billback', align: 'start', show: true },
        { text: 'DSD OI', sortable: true, filterable: true, class: 'accent white--text px-2', value: 'dsd_oi_billback', align: 'start', show: true },
        { text: 'ELDC BB', sortable: true, filterable: true, class: 'accent white--text px-2', value: 'edlc_billback', align: 'start', show: true },
        { text: 'TPR BB', sortable: true, filterable: true, class: 'accent white--text px-2', value: 'tpr_billback', align: 'start', show: true },
        { text: 'Ad BB', sortable: true, filterable: true, class: 'accent white--text px-2', value: 'ad_billback', align: 'start', show: true },
        { text: 'EDLC Scan', sortable: true, filterable: true, class: 'accent white--text px-2', value: 'edlc_scan', align: 'start', show: true },
        { text: 'EDLC ePay', sortable: true, filterable: true, class: 'accent white--text px-2', value: 'epay_edlc_scan', align: 'start', show: true },
        { text: 'EDLC Compete', sortable: true, filterable: true, class: 'accent white--text px-2', value: 'edlc_scan_compete', align: 'start', show: true },
        { text: 'TPR Scan', sortable: true, filterable: true, class: 'accent white--text px-2', value: 'tpr_scan', align: 'start', show: true },
        { text: 'TPR ePay', sortable: true, filterable: true, class: 'accent white--text px-2', value: 'epay_tpr_scan', align: 'start', show: true },
        { text: 'TPR Compete', sortable: true, filterable: true, class: 'accent white--text px-2', value: 'tpr_scan_compete', align: 'start', show: true },
        { text: 'Ad Scan', sortable: true, filterable: true, class: 'accent white--text px-2', value: 'ad_scan', align: 'start', show: true },
        { text: 'Ad Scan ePay', sortable: true, filterable: true, class: 'accent white--text px-2', value: 'epay_ad_scan', align: 'start', show: true },
        { text: 'Ad Scan Compete', sortable: true, filterable: true, class: 'accent white--text px-2', value: 'ad_scan_compete', align: 'start', show: true },
        { text: 'Net Unit Cost', sortable: true, filterable: true, class: 'accent white--text px-2', value: 'net_unit_cost', align: 'start', show: true, width: 75 },
        { text: 'Ad Count / Ad Retail', sortable: true, filterable: true, class: 'accent white--text noWrap px-2', value: 'count_and_retail', align: 'center', show: true, width: 230 }, // Changeable SRP
        { text: 'SP/', sortable: true, filterable: true, class: 'accent white--text noWrap px-2', value: 'regular_retail_count', align: 'start', show: true },
        { text: 'Regular Retail', sortable: true, filterable: true, class: 'accent white--text px-2', value: 'regular_retail', align: 'start', show: true },
        { text: 'Mvmt', sortable: true, filterable: true, class: 'accent white--text noWrap px-2', value: 'last_90_days_shipped_qty', align: 'start', show: true },
        { text: 'Gross Profit', sortable: true, filterable: true, class: 'accent white--text px-2', value: 'ad_gross_profit_percent', align: 'start', show: true },
        { text: 'FEATURE NOTES', sortable: true, filterable: true, class: 'accent white--text', value: 'feature_notes', show: true, hide_from_table: true },
        { text: 'TPR DATES', sortable: true, filterable: true, class: 'accent white--text noWrap px-2', value: 'tpr_dates', align: 'center', show: true },
        { text: 'FEATURE DESCRIPTION', sortable: true, filterable: true, class: 'accent white--text', value: 'feature_text', show: true, hide_from_table: true }
      ],
      selectedHeaders: []
    }
  },

  watch: {
    adPlanner: {
      handler(newValue) {
        if (newValue) {
          sessionStorage.setItem("ad_planner", JSON.stringify(this.adPlanner))
        }
      },
      deep: true
    },
    adPlannerParty: {
      handler(newValue) {
        if (newValue) {
          sessionStorage.setItem("ad_planner_party", JSON.stringify(this.adPlannerParty))
        }
      },
      deep: true
    },

    storeParties: {
      handler(newValue) {
        if (newValue) {
          sessionStorage.setItem("store_parties", JSON.stringify(this.storeParties))
        }
      },
      deep: true
    },

    itemFilters: {
      handler: _.debounce(function (newValue) {
        this.items = []
        this.loading = true
        if (newValue) {
          if (this.page > 1) {
            this.page = 1
          } else {
            this.getAdPlannerItems()
          }
        }
      }, 500),
      deep: true
    }

    // TODO:
    //  Add watch for unsaved changes to store those in session storage so they don't get lost on a refresh
    //  Or just add a warning on page close/change/refresh that any changes will be lost
  },

  async created () {
    this.loading = true
    this.nonTableHeight = 224
    // Route Params
    const { store_id, ad_id, meat_grid, adPlanner, ad_group, store_name, storeParties, edge_subscriber } = this.$route.params
    this.adPlanner = adPlanner || JSON.parse(sessionStorage.getItem("ad_planner"))
    this.ad_id = ad_id || JSON.parse(sessionStorage.getItem('ad_id'))
    this.adPlannerParty = ad_group || JSON.parse(sessionStorage.getItem("ad_planner_party"))
    this.store_id = store_id || sessionStorage.getItem("store_id")
    this.store_name = store_name || sessionStorage.getItem("store_name")
    this.storeParties = storeParties || JSON.parse(sessionStorage.getItem("store_parties"))
    this.edgeSubscriber = edge_subscriber || JSON.parse(sessionStorage.getItem('edge_subscriber'))
    this.meat_grid = meat_grid || JSON.parse(sessionStorage.getItem('meat_grid')) || false

    // Set vital params if route visited via direct link/bookmark
    const navigatedFromAdManager = Boolean(this.$route.params.store_name)
    if (!navigatedFromAdManager) {
      await this.setVitalState()
    }

    const savedHeaders = sessionStorage.getItem("user_headers")

    if (savedHeaders) {
      try {
        this.headers = JSON.parse(savedHeaders)
      } catch (error) {
        console.error("Unable to parse saved user headers:", error)
      }
    }

    this.tableChanged = this.tableChange(this.getAdPlannerItems)
    this.getDepartments()
    this.getPages()
    this.getFeatureText()
    this.getFeatureType()
    this.getAdPlannerItems()
    this.getPublishedChanges()
  },

  computed: {
    breadcrumbs() {
      return [
        {
          text: this.adPlannerParty?.name,
          exact: true,
          href: '/ad-planner-edit'
        },
        {
          text: this.adPlanner?.ad_name,
          exact: true,
          disabled: true
        },
        {
          text: this.store_name,
          exact: true,
          disabled: true
        }
      ]
    },

    readOnly () {
      return this.hasOrderGridReadOnlyAccess
    },

    totalItems () {
      return this.items.length
    },

    disablePrevious () {
      return (this.page - 1) * this.pageSize === 0
    },

    disableNext () {
      return this.totalItems < this.pageSize
    },

    displayHeaders () {
      const visable_headers = [{ sortable: false, value: 'data-table-select', class: 'accent white--text', show: true }, ...this.headers]
      return visable_headers.filter(header => header.show === true && header.hide_from_table !== true)
    },

    itemFilters () {
      return { ...this.searchParams }
    },

    groupedItems() {
      return this.items.map(item => {
        const groupName = `${item.page_name} | ${item.position_name} | ${item.feature_text}`
        const groupKey = item.groupKey = `${item.page_display_order.toString().padStart(6, '0')}${item.position_display_order.toString().padStart(8, '0')}`
        return {
          ...item,
          groupName,
          groupKey
        }
      })
    },

    currentDate() {
      return moment().format(this.$dateConfig.date_format)
    },

    hasUnsavedChanges() {
      return this.unsaved_changes.length > 0
    },

    hasSelectedItems() {
      return this.selectedItems.length > 0
    },

    isEditingDisabled() {
      return this.loading || this.currentDate >= this.adPlanner.ad_start_date
    },
    
    srpRules() {
      const rules = []

      const valueRequired = v => !!v || 'SRP Required'
      rules.push(valueRequired)

      const positiveValue = v => parseFloat(v) >= 0 || 'Invalid SRP'
      rules.push(positiveValue)

      return rules
    },

    countRules() {
      const rules = []

      const valueRequired = v => !!v || 'Count Required'
      rules.push(valueRequired)

      const positiveValue = v => v > 0 || 'Invalid Count'
      rules.push(positiveValue)

      return rules
    },

    suggestedOrderStore() {
      return this.$auth.tenant === 'alliance-retail-group' && this.edgeSubscriber
    },

    hasPublishedChanges() {
      return this.publishedChanges.length > 0
    },

    orderGridClosed() {
      const now = moment().format('YYYY-MM-DD')
      const { lastOrderDate } = this.getFinalOrderDates(this.adPlanner.ad_start_date, this.suggestedOrderStore, this.meat_grid)

      return lastOrderDate <= now
    },

    disableSendToGrid() {
      return this.orderGridClosed || this.hasPublishedChanges || this.loading
    },

    tooltipText() {
      if (this.orderGridClosed) {
        return "Related order grid is closed"
      }
      if (this.hasPublishedChanges) {
        return "Saved changes are currently being processed for the order grid"
      }
      return "Send saved changes to Order Grid"
    }
  },

  methods: {
    rowHighlight(item) {
      return item.item_id === this.highlighted.item_id ? 'primary white--text' : ''
    },

    async getStore() {
      // Set Store Data
      if (this.limitAccessByRelatedStores && !this.activeUserRelatedPartyIds.includes(this.store_id)) {
        this.handleError("User does not have access to selected store")
      } else {
        let resp = await Party.get(this.store_id)
        const partial_store_name = resp.data.name

        // Get Store Attributes
        resp = await Attributes.getStoreAttributes(this.store_id, 100, 0)

        // Format store name
        const wholesaler_store_number = resp.data.find((key_val) => key_val.attr.id === 'WHOLESALER_STORE_NUMBER')
        this.store_name = `#${wholesaler_store_number.value} ${partial_store_name}`

        // Get Edge Sub
        const now = moment()
        const edge_attrs = resp.data.filter((key_val) => key_val.attr.id === 'EDGE_SUBSCRIPTION')
        const active_edge_attr = edge_attrs.find((attr) => {
          return moment(attr.start_dt) <= now && (attr.end_dt === null || moment(attr.end_dt) >= now)
        })
        this.edgeSubscriber = active_edge_attr?.value.toLowerCase() === 'true'
      }
    },

    async setVitalState() {
      // Set the minimum params necessary to load the order grid
      this.store_id = this.$route.params.store_id
      this.ad_id = this.$route.params.ad_id
      this.meat_grid = this.$route.params.meat_grid === 'true'
      sessionStorage.setItem("store_id", JSON.stringify(this.store_id))
      sessionStorage.setItem("ad_id", JSON.stringify(this.ad_id))
      sessionStorage.setItem("meat_grid", JSON.stringify(this.meat_grid))

      try {
        // Set Ad Data
        const resp = await AdRetails.getAd(this.ad_id)
        const adPlannerPartyId = resp.data.ad_party_id

        this.adPlanner = {
          ad_end_date: resp.data.ad_end_date,
          ad_start_date: resp.data.ad_start_date,
          ad_id: resp.data.id,
          ad_name: resp.data.ad_name,
          created_by: resp.data.audit.created_by,
          created_on: resp.data.audit.created_on,
          meat_grid: this.meat_grid
        }

        // Set Store Data
        await this.getStore()
        sessionStorage.setItem("selected_store", JSON.stringify({
          id: this.store_id,
          name: this.store_name,
          edge_subscriber: this.edgeSubscriber
        }))
        sessionStorage.setItem("edge_subscriber", JSON.stringify(this.edgeSubscriber))

        // Populate adPlanner data
        if (this.suggestedOrderStore) {
          this.adPlanner.meat_grid = JSON.parse(this.$route.params.meat_grid)
          this.adPlanner.ad_name += this.adPlanner.meat_grid ? " Meat" : " Grocery"
        }

        // Set Ad Planner Party
        const searchTerm = 'AD_GROUP ' + adPlannerPartyId
        const adGroup = await this.queryParties(searchTerm)
        this.adPlannerParty = adGroup[0]
        sessionStorage.setItem("ad_planner_party", JSON.stringify(this.adPlannerParty))
        sessionStorage.setItem("selected_group", JSON.stringify(this.adPlannerParty))
      } catch (err) {
        this.handleError(err)
      }
    },

    async getAdPlannerItems() {
      this.loading = true
      const params = {
        party_id: this.adPlannerParty.id,
        store_id: this.store_id,
        offset: this.from,
        limit: this.pageSize,
        meat_grid: this.meat_grid,
        edge_store: this.edgeSubscriber,
        ...this.searchParams
      }
      // Normalize 6-digit item IDs to 7-digit by prepending 0
      if (params.item_id && params.item_id.length === 6) {
        params.item_id = '0' + params.item_id
      }

      try {
        const { data } = await AdRetails.getRetailAdEditItems(this.ad_id, params)
        if (data.length > 0) {
          this.items = data

          this.items.forEach(item => {
            item.ad_retail = this.formatPrice(item.ad_retail)
            item.base_ad_retail = item.ad_retail
            item.base_ad_retail_count = item.ad_retail_count
            this.updateGrossProfit(item)
          })
        }
      } catch (err) {
        this.handleError(err)
      } finally {
        this.loading = false
      }
    },

    handleChange(item, event) {
      if (!event || !event.target) {
        console.error("Invalid event object:", event)
        return
      }
      this.updateUnsavedChanges(item)
    },

    updateUnsavedChanges(item) {
      this.updateGrossProfit(item)
      const existing_change = this.unsaved_changes.find(change => change.item_id === item.item_id)

      if (existing_change) {
        existing_change.ad_retail = item.ad_retail
        existing_change.ad_retail_count = item.ad_retail_count
        existing_change.ad_gross_profit_percent = item.ad_gross_profit_percent
      } else {
        const new_change = {
          change_id: item.change_id,
          item_id: item.item_id,
          ad_plan_id: item.ad_plan_id,
          store_id: this.store_id,
          feature_id: item.feature_id,
          ad_retail: item.ad_retail,
          ad_retail_count: item.ad_retail_count,
          ad_gross_profit_percent: item.ad_gross_profit_percent
        }
        this.unsaved_changes.push(new_change)
      }
    },

    async saveChanges() {      
      try {
        const { data } = await AdRetails.updateRetailAd(this.ad_id, this.unsaved_changes)
        if (data.updated > 0) {
          this.emitAlert(true, 'success', 'Changes have been saved!', null, 500)
          this.unsaved_changes = []
          this.getAdPlannerItems()
        }
      } catch (err) {
        this.handleError(err)
      }
    },

    undoChanges(item) {
      this.unsaved_changes = this.unsaved_changes.filter(change => change.item_id !== item.item_id)
      item.ad_retail = item.base_ad_retail
      item.ad_retail_count = item.base_ad_retail_count
      this.updateGrossProfit(item)
    },

    updateGrossProfit(item) {
      item.ad_gross_profit_percent = this.calculateGrossProfit(item)
    },

    calculateGrossProfit(item) {
      if (item.ad_retail_count <= 0 || item.ad_retail <= 0) {
        return 0
      }

      const allowances = Number(item.amap) + Number(item.early_buy_billback) + Number(item.ad_billback) + Number(item.tpr_billback) + Number(item.edlc_billback)
      const scanAllowances = Number(item.ad_scan) + Number(item.tpr_scan) + Number(item.edlc_scan) + Number(item.epay_ad_scan) + Number(item.ad_scan_compete) + Number(item.epay_tpr_scan) + Number(item.tpr_scan_compete) + Number(item.epay_edlc_scan) + Number(item.edlc_scan_compete)

      const unitCost = ((Number(item.net_case_cost) - allowances) / Number(item.qty_per_case)) - scanAllowances
      const retailValue = Number(item.ad_retail) / Number(item.ad_retail_count)
      const grossProfit = ((retailValue - unitCost) / retailValue) * 10000

      return Math.round(grossProfit) / 100
    },

    bulkUpdate() {
      this.loading = true
      this.selectedItems.forEach(item => {
        item.ad_retail = this.bulkSRP
        item.ad_retail_count = this.bulkCount
        this.updateUnsavedChanges(item)
      })
      this.loading = false
      this.closeBulkEdit()
      this.selectedItems = []
      this.emitAlert(true, 'info', 'Multiple item values have been updated, save to apply changes', null, 500)
    },

    handleBlur(item, event) {
      if (!item || !event || !event.target) {
        return
      }

      const input_value = event.target.value
      const formatted_value = this.formatPrice(input_value)
      item.ad_retail = formatted_value
    },

    formatPrice(value) {
      if (value === '' || value === null || value === undefined) {
        return value
      }

      return parseFloat(value).toFixed(2)
    },

    async getPages() {
      try {
        const { data } = await AdRetails.getPages(this.ad_id, false, this.suggestedOrderStore, this.meat_grid)
        if (data.length > 0) {
          this.pages = data
        }
      } catch (err) {
        this.handleError(err)
      }
    },

    async getDepartments() {
      try {
        const { data } = await AdRetails.getDepartments(this.ad_id, false, this.suggestedOrderStore, this.meat_grid)
        if (data.length > 0) {
          this.departments = data
        }
      } catch (err) {
        this.handleError(err)
      }
    },

    async getFeatureText() {
      try {
        const { data } = await AdRetails.getFeatureText(this.ad_id, false, this.suggestedOrderStore, this.meat_grid)
        if (data.length > 0) {
          this.feature_text = data
        }
      } catch (err) {
        this.handleError(err)
      }
    },

    async getFeatureType() {
      try {
        const { data } = await AdRetails.getFeatureType(this.ad_id, false, this.suggestedOrderStore, this.meat_grid)
        if (data.length > 0) {
          this.feature_type = data
        }
      } catch (err) {
        this.handleError(err)
      }
    },

    async closeModal() {
      this.showExportModal = false
    },

    closeBulkEdit() {
      this.bulkEditMenu = false
      this.bulkCount = ''
      this.bulkSRP = ''
    },

    async exportCSV() {
      const params = {
        ad_group_name: this.adPlannerParty.party_name,
        ad_start_dt: this.adPlanner.ad_start_date,
        ad_group_id: this.adPlannerParty.party_id,
        store_id: this.store_id
      }

      const export_headers = this.headers.filter(header => header.value !== 'data-table-select' && header.show === true).map(header => header.value)

      if (export_headers.includes('count_and_retail')) {
        const header_index = export_headers.indexOf('count_and_retail')
        export_headers.splice(header_index, 1, "ad_retail_count", "ad_retail")
      }

      if (export_headers.includes('amap')) {
        const header_index = export_headers.indexOf('amap')
        export_headers.splice(header_index, 1, "amap_billback")
      }

      try {
        const { data } = await AdRetails.exportEditCSV(this.ad_id, params, export_headers)

        const anchor = document.createElement('a')
        anchor.href = data
        anchor.download = "Test Download"
        anchor.click()
      } catch (err) {
        this.handleError(err)
      }
    }, 

    openColumnVisability() {
      this.columnVisability = true
      this.selectedHeaders = JSON.parse(JSON.stringify(this.headers))
    },

    closeColumnVisability() {
      this.columnVisability = false
      this.selectedHeaders = []
    },

    saveCustomHeaders() {
      sessionStorage.setItem("user_headers", JSON.stringify(this.selectedHeaders))
      this.headers = JSON.parse(JSON.stringify(this.selectedHeaders))
      this.columnVisability = false
    },

    resetCustomHeaders() {
      this.selectedHeaders.forEach(header => header.show = true)
    },

    async publishToOrderGrid() {
      try {
        const params = {
          meat_grid: this.meat_grid,
          edge_store: this.suggestedOrderStore
        }

        const { data } = await AdRetails.publishChangesToOrderGrid(this.ad_id, this.store_id, params)
        if (data === 'PROCESSED') {
          this.emitAlert(true, 'success', 'Changes have been sent to the order grid.', null, 1000)
        } else if (data === 'PUBLISHED') {
          this.emitAlert(true, 'info', 'Changes have begun getting processed for the order grid.', null, 1000)
        } else if (data === 'NONE') {
          this.emitAlert(true, 'info', 'No saved changes to send to order grid.', null, 1000)
        }
      } catch (error) {
        console.error("Encountered an error while publishing changes to order grid:", error)
      } finally {
        this.getPublishedChanges()
      }
    },

    async getPublishedChanges() {
      try {
        const params = {
          meat_grid: this.meat_grid,
          status: 'PUBLISHED'
        }

        const { data } = await AdRetails.getChanges(this.ad_id, this.store_id, params)
        if (data.length > 0) {
          this.publishedChanges = data
        }
      } catch (error) {
        console.error("Unable to retrieve published change records:", error)
      }
    }

  }
}
</script>
<style>
.theme--light.v-data-table .v-data-table-header th.sortable .v-data-table-header__icon {
  color: #fff !important;
}

.v-data-table > .v-data-table__wrapper tbody tr.v-data-table__expanded__content {
  box-shadow: none !important;
}

.v-data-table__expanded__row > .text-start {
  border-top: 1px solid #9e9e9e !important;
  border-bottom: none !important;
  /* border-top: 2px solid #385f73 !important; */
}

.noWrap {
  white-space: nowrap;
}

.active {
  box-shadow: 0px 2px 10px 3px rgba(120, 120, 120, 0.75);
  background: #385f73;
  color: white !important;
}

.filtered {
  background: #ffc107 !important;
  color: white !important;
}

.icon-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #ff8f00;
  color: white !important;
  border: 5px solid white;
  box-sizing: border-box;
}

.icon-circle-active {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #ff8f00;
  color: white !important;
  border: 5px solid #2589bd;
  box-sizing: border-box;
}
</style>
